import api from '@/store/api';
import { athena, boro } from '@/store/api/instances';
import type { Issue } from '@/types';
import type { ApiListingRequest, ApiResponse } from '@bitstopco/bitstop-theme';
import { type SkipToken, skipToken } from '@reduxjs/toolkit/query';

export interface IssueResources {
  statuses: string[];
  record_statuses: string[];
  priorities: string[];
  types: string[];
  severities: string[];
  event_types?: string[];
}

export const issuesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getIssues: build.query<ApiResponse<Issue[]>, ApiListingRequest>({
      query: (data) => {
        return athena({
          url: 'v2/operations/issues',
          method: 'POST',
          data,
        });
      },
      providesTags: ['Issue'],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        // Serialize the query args to ensure proper cache key generation
        // This ensures that different filter combinations create different cache entries
        return `${endpointName}(${JSON.stringify(queryArgs)})`;
      },
      merge: (_currentCache, newItems) => {
        // Don't merge, always use new data
        return newItems;
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        // Force refetch if the payload has changed
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
      transformResponse: (response: ApiResponse<any[]>) => {
        // Ensure resolved_by_events and resolved_by_event_ids are properly included
        if (response?.data) {
          response.data = response.data.map((issue: any) => ({
            ...issue,
            resolved_by_event_ids: issue.resolved_by_event_ids || [],
            resolved_by_events: issue.resolved_by_events || [],
          }));
        }
        return response as ApiResponse<Issue[]>;
      },
    }),
    getIssueResources: build.query<ApiResponse<IssueResources>, void>({
      query: () => {
        return athena({
          url: 'v2/Resources/issues',
          method: 'GET',
        });
      },
    }),
    getIssuesStats: build.query<ApiResponse<any>, { from: string; to: string }>({
      query: ({ from, to }) => {
        return athena({
          url: `v2/operations/issues/stats?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
          method: 'GET',
        });
      },
    }),
    exportIssues: build.mutation<BlobPart, { type: string; data: ApiListingRequest }>({
      query: ({ type, data }) => {
        return athena({
          url: `v2/Operations/issues/${type}`,
          method: 'POST',
          data,
          responseType: 'blob',
        });
      },
    }),
    createIssue: build.mutation<
      ApiResponse<Issue>,
      {
        atm_id: number;
        issue_type: string;
        severity?: string;
        linked_service_ticket_id?: string;
      }
    >({
      query: (data) =>
        boro({
          url: 'v2/Operations/issues',
          method: 'POST',
          data,
        }),
      invalidatesTags: ['Issue'],
    }),
  }),
});

export const useGetIssues = (
  data: ApiListingRequest | SkipToken,
  options?: { refetchOnMountOrArgChange?: boolean },
) => {
  return issuesApi.useGetIssuesQuery(data, {
    skip: data === skipToken,
    refetchOnMountOrArgChange: options?.refetchOnMountOrArgChange ?? true,
  });
};

export const useGetIssuesStats = (params: { from: string; to: string } | SkipToken) => {
  return issuesApi.useGetIssuesStatsQuery(params, {
    skip: params === skipToken,
  });
};

export const useExportIssues = () => {
  return issuesApi.useExportIssuesMutation();
};

export const { useGetIssueResourcesQuery, useCreateIssueMutation } = issuesApi;
